import { useEffect, useRef } from "react";
import { usePaystackPayment } from "react-paystack";
import { PaystackProps } from "react-paystack/dist/types";
import { useExternalPayment } from "../components/Dialog/ExternalPayment";

type Callbacks = {
  onSuccess: () => void;
  onClose: () => void;
  onError: (error: unknown) => void;
};
/** Keeps the underlying dialog mounted while releasing its focus/overlay locks. */
export function useModalPaystackPayment(
  config: PaystackProps | null,
  callbacks: Callbacks
) {
  const initialize = usePaystackPayment(
    config || { reference: "", email: "", amount: 0, publicKey: "" }
  );
  const latest = useRef({ initialize, callbacks });
  useEffect(() => {
    latest.current = { initialize, callbacks };
  });
  const suspend = useExternalPayment();
  useEffect(() => {
    if (!config) return;
    let timer: ReturnType<typeof setTimeout>;
    let release = () => {};
    let disposed = false;
    let finished = false;
    let attempts = 0;
    const complete = (callback: () => void) => {
      if (disposed || finished) return;
      finished = true;
      release();
      callback();
    };
    const open = () => {
      if (disposed) return;
      // react-paystack loads its script asynchronously and otherwise silently no-ops.
      if (typeof window.PaystackPop?.setup !== "function") {
        if (++attempts >= 100) {
          complete(() =>
            latest.current.callbacks.onError(
              new Error("The payment window couldn’t load. Please try again.")
            )
          );
          return;
        }
        timer = setTimeout(open, 100);
        return;
      }
      release = suspend();
      // Give Radix a commit to release its trap before Paystack focuses its iframe.
      timer = setTimeout(() => {
        if (disposed) return;
        try {
          latest.current.initialize(
            () => complete(() => latest.current.callbacks.onSuccess()),
            () => complete(() => latest.current.callbacks.onClose())
          );
        } catch (error) {
          complete(() => latest.current.callbacks.onError(error));
        }
      }, 0);
    };
    open();
    return () => {
      disposed = true;
      clearTimeout(timer);
      release();
    };
  }, [config, suspend]);
}
